import { fetchConfigCountries } from '@shared-core/features/config/actions'
import { selectedConfigCountries } from '@shared-core/features/config/selectors'
import { useI18N } from '@shared-core/hooks/use-i18n'
import locales from '@shared-core/locales/countries.dynamic.i18n.json'
import { useAppDispatch, useAppSelector } from '@shared-core/store/hooks'
import { type Country, isCountryWithFlag } from '@shared-core/utils/countries-meta'
import type { CountryCode } from '@shared-core/utils/phone'
import type { flags } from '@shared-images/flags'
import { useCallback, useEffect, useMemo } from 'react'

const EXCLUDED_COUNTRY_CODES = new Set<CountryCode>(['ua', 'us', 'af', 'ir', 'kp', 'mm', 'sy', 'ye'])

const isAllowedCountryCode = (code: string): code is CountryCode =>
  !EXCLUDED_COUNTRY_CODES.has(code.trim().toLowerCase() as CountryCode)

type UseCountriesOptions = {
  includeAllByAA?: boolean
}

const LOCALE_COUNTRY_KEYS = new Set(
  Object.values(locales).flatMap((locale) =>
    Object.keys((locale ?? {}) as Record<string, string>).filter((key) => key.startsWith('country-')),
  ),
)

const BASE_LOCALE = locales.en ?? locales.ru ?? (Object.values(locales)[0] as Record<string, string> | undefined)
const LOCALE_COUNTRY_CODES: (CountryCode & keyof typeof flags)[] = BASE_LOCALE
  ? Object.keys(BASE_LOCALE)
      .filter((key) => key.startsWith('country-'))
      .map((key) => key.replace('country-', '').toLowerCase())
      .filter(isCountryWithFlag)
      .filter(isAllowedCountryCode)
  : []

export function useCountryNameByCode(code?: string, iso3?: string): string {
  const { t } = useI18N(locales)

  return useMemo(() => {
    const normalizedCode = code?.trim().toLowerCase() ?? ''

    if (!normalizedCode) return ''

    const localeKey = `country-${normalizedCode}`

    if (LOCALE_COUNTRY_KEYS.has(localeKey)) {
      return t(localeKey as 'country-ru')
    }

    return iso3?.trim().toUpperCase() || normalizedCode.toUpperCase()
  }, [code, iso3, t])
}

export function useCountries(options?: UseCountriesOptions): Country[] {
  const { includeAllByAA = true } = options ?? {}
  const dispatch = useAppDispatch()
  const { t } = useI18N(locales)
  const configCountries = useAppSelector(selectedConfigCountries)

  // Подтягиваем backend-конфиг стран, если его ещё нет в store.
  useEffect(() => {
    if (!configCountries.length) {
      void dispatch(fetchConfigCountries())
    }
  }, [configCountries.length, dispatch])

  const resolveCountryName = useCallback(
    (code: string, iso3?: string) => {
      const localeKey = `country-${code}`

      if (LOCALE_COUNTRY_KEYS.has(localeKey)) {
        return t(localeKey as 'country-ru')
      }

      return iso3?.trim().toUpperCase() || code.toUpperCase()
    },
    [t],
  )

  const localeCountryCodes = LOCALE_COUNTRY_CODES

  const fallbackCountries = useMemo<Country[]>(
    () =>
      localeCountryCodes.map((code) => ({
        code,
        name: resolveCountryName(code),
        flagKey: code,
      })),
    [localeCountryCodes, resolveCountryName],
  )

  const hasAllCountriesCode = useMemo(
    () => configCountries.some((item) => item.iso2.trim().toUpperCase() === 'AA'),
    [configCountries],
  )

  const countriesFromConfig = useMemo<Country[]>(() => {
    if (includeAllByAA && hasAllCountriesCode) return fallbackCountries

    const uniqueCodes = new Set<string>()

    // В обычном режиме строим список только из стран, пришедших с backend,
    // с дедупликацией и фильтром по доступным флагам в приложении.
    return configCountries.reduce<Country[]>((acc, item) => {
      const code = item.iso2.trim().toLowerCase()

      if (!isCountryWithFlag(code) || uniqueCodes.has(code) || !isAllowedCountryCode(code)) return acc

      uniqueCodes.add(code)
      acc.push({
        code: code as CountryCode,
        name: resolveCountryName(code, item.iso3),
        flagKey: code,
      })
      return acc
    }, [])
  }, [configCountries, fallbackCountries, hasAllCountriesCode, includeAllByAA, resolveCountryName])

  return countriesFromConfig
}
